import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../shell/auth/auth.service';
import { AuthSessionService } from '../../shell/auth/auth-session.service';
import { ServicesGateDutyService } from '../services-gate-duty.service';
import { readFacilityApiError } from './facility-api-error.util';
import type {
  CreateAdHocWorkOrderRequest,
  FmWorkOrderCreateAssigneesDto,
  FmWorkOrderCreateContextDto,
  FmWorkOrderDto,
  PageResponseFmWorkOrderDto,
  RecordChecklistResponsesRequest,
  VendorWorkOrderListQuery,
  WorkOrderZoneScanRequest,
} from './services-work-order.models';

interface OneApiResponse<T = unknown> {
  data?: T;
  message?: string;
  code?: number;
}

const BASE = `${environment.facilityApiUrl}/v1/facility/vendor-portal`;

function unwrapData<T>(res: OneApiResponse<T> | null | undefined): T {
  return (res?.data ?? null) as T;
}

function unwrapVendorWorkOrderPage(res: unknown): PageResponseFmWorkOrderDto {
  if (res == null || typeof res !== 'object') {
    return {};
  }
  if (Array.isArray(res)) {
    return { content: res as FmWorkOrderDto[], totalElements: res.length };
  }
  const root = res as Record<string, unknown>;
  const data = root['data'];
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    return data as PageResponseFmWorkOrderDto;
  }
  return root as PageResponseFmWorkOrderDto;
}

function listParams(projectId: number, query?: VendorWorkOrderListQuery): HttpParams {
  let params = new HttpParams().set('projectId', String(projectId));
  if (query?.workOrderType) {
    params = params.set('workOrderType', query.workOrderType);
  }
  if (query?.statuses?.length) {
    for (const s of query.statuses) {
      params = params.append('statuses', s);
    }
  } else if (query?.status) {
    params = params.set('status', query.status);
  }
  if (query?.serviceCategory?.trim()) {
    params = params.set('serviceCategory', query.serviceCategory.trim());
  }
  if (query?.search?.trim()) {
    params = params.set('search', query.search.trim());
  }
  if (query?.occurrenceFrom) {
    params = params.set('occurrenceFrom', query.occurrenceFrom);
  }
  if (query?.occurrenceTo) {
    params = params.set('occurrenceTo', query.occurrenceTo);
  }
  if (query?.shiftLabel?.trim()) {
    params = params.set('shiftLabel', query.shiftLabel.trim());
  }
  if (query?.page != null) {
    params = params.set('page', String(query.page));
  }
  if (query?.size != null) {
    params = params.set('size', String(query.size));
  }
  return params;
}

@Injectable({ providedIn: 'root' })
export class ServicesWorkOrdersApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly session = inject(AuthSessionService);
  private readonly gateDuty = inject(ServicesGateDutyService);

  isF1Manager(): boolean {
    return (this.session.snapshot?.authorityTier ?? '').toUpperCase() === 'F1';
  }

  async listWorkOrders(query: VendorWorkOrderListQuery): Promise<PageResponseFmWorkOrderDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const tierPath = this.isF1Manager() ? 'f1' : 'f2';
    const res = await firstValueFrom(
      this.http.get<OneApiResponse>(`${BASE}/${tierPath}/work-orders`, {
        headers: { 'X-Organization-Id': String(orgId) },
        params: listParams(projectId, query),
      }),
    );
    return unwrapVendorWorkOrderPage(unwrapData(res));
  }

  async getF1CreateContext(): Promise<FmWorkOrderCreateContextDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    try {
      const res = await firstValueFrom(
        this.http.get<OneApiResponse<FmWorkOrderCreateContextDto>>(`${BASE}/f1/work-orders/create-context`, {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId) },
        }),
      );
      return unwrapData(res) ?? {};
    } catch (err) {
      throw new Error(readFacilityApiError(err, 'Could not load create form'));
    }
  }

  async getF1CreateAssignees(serviceCategory: string): Promise<FmWorkOrderCreateAssigneesDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    try {
      const res = await firstValueFrom(
        this.http.get<OneApiResponse<FmWorkOrderCreateAssigneesDto>>(`${BASE}/f1/work-orders/create-assignees`, {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId), serviceCategory },
        }),
      );
      return unwrapData(res) ?? {};
    } catch (err) {
      throw new Error(readFacilityApiError(err, 'Could not load positions'));
    }
  }

  async createF1AdHocWorkOrder(body: CreateAdHocWorkOrderRequest): Promise<FmWorkOrderDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    try {
      const res = await firstValueFrom(
        this.http.post<OneApiResponse<FmWorkOrderDto>>(`${BASE}/f1/work-orders/create-ad-hoc`, body, {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId) },
        }),
      );
      const data = unwrapData(res);
      if (!data) {
        throw new Error('Create did not return a work order');
      }
      return data;
    } catch (err) {
      throw new Error(readFacilityApiError(err, 'Could not create work order'));
    }
  }

  async getWorkOrder(workOrderId: number): Promise<FmWorkOrderDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const res = await firstValueFrom(
      this.http.get<OneApiResponse<FmWorkOrderDto>>(
        `${BASE}/${this.workOrderTierPath()}/work-orders/${workOrderId}`,
        {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId) },
        },
      ),
    );
    const data = unwrapData(res);
    if (!data) {
      throw new Error('Work order not found');
    }
    return data;
  }

  async acknowledgeWorkOrder(workOrderId: number): Promise<FmWorkOrderDto> {
    return this.postAction(workOrderId, 'acknowledge');
  }

  async recordZoneScan(workOrderId: number, body: WorkOrderZoneScanRequest): Promise<FmWorkOrderDto> {
    return this.postAction(workOrderId, 'zone-scan', body);
  }

  async skipZoneScan(
    workOrderId: number,
    body: { projectZoneId: number; reason: string },
  ): Promise<FmWorkOrderDto> {
    return this.postAction(workOrderId, 'zone-scan/skip', body);
  }

  async submitChecklist(workOrderId: number, body: RecordChecklistResponsesRequest): Promise<FmWorkOrderDto> {
    return this.postAction(workOrderId, 'checklist/submit', body);
  }

  async requestChecklistPhotoUploadUrl(
    workOrderId: number,
    checklistOrder: number,
    contentType: string,
  ): Promise<{ uploadUrl: string; uploadMethod: 'PUT' | 'POST'; imageUrl: string; contentType: string }> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const res = await firstValueFrom(
      this.http.post<OneApiResponse>(
        `${BASE}/${this.workOrderTierPath()}/work-orders/${workOrderId}/checklist-photo/upload-url`,
        { checklistOrder, contentType },
        {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId) },
        },
      ),
    );
    const raw = unwrapData(res) as {
      uploadUrl?: string;
      uploadMethod?: string;
      imageUrl?: string;
      contentType?: string;
    };
    const uploadMethod = raw.uploadMethod === 'POST' ? 'POST' : 'PUT';
    const imageUrl = raw.imageUrl ?? '';
    let uploadUrl = raw.uploadUrl ?? '';
    if (uploadMethod === 'POST' && !uploadUrl) {
      uploadUrl = `${BASE}/${this.workOrderTierPath()}/work-orders/${workOrderId}/checklist-photo?projectId=${projectId}&checklistOrder=${checklistOrder}`;
    }
    return {
      uploadUrl,
      uploadMethod,
      imageUrl,
      contentType: raw.contentType?.trim() || contentType,
    };
  }

  async requestPatrolCheckpointPhotoUploadUrl(
    workOrderId: number,
    projectZoneId: number,
    contentType: string,
  ): Promise<{ uploadUrl: string; uploadMethod: 'PUT' | 'POST'; photoPdfUrl: string; contentType: string }> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const res = await firstValueFrom(
      this.http.post<OneApiResponse>(
        `${BASE}/f2/work-orders/${workOrderId}/patrol-checkpoint-photo/upload-url`,
        { projectZoneId, contentType },
        {
          headers: { 'X-Organization-Id': String(orgId) },
          params: { projectId: String(projectId) },
        },
      ),
    );
    const raw = unwrapData(res) as {
      uploadUrl?: string;
      uploadMethod?: string;
      photoPdfUrl?: string;
      contentType?: string;
    };
    const uploadMethod = raw.uploadMethod === 'POST' ? 'POST' : 'PUT';
    const photoPdfUrl = raw.photoPdfUrl ?? '';
    let uploadUrl = raw.uploadUrl ?? '';
    if (uploadMethod === 'POST' && !uploadUrl) {
      uploadUrl = `${BASE}/f2/work-orders/${workOrderId}/patrol-checkpoint-photo?projectId=${projectId}&projectZoneId=${projectZoneId}`;
    }
    if (!photoPdfUrl) {
      throw new Error('Patrol photo upload did not return photoPdfUrl');
    }
    return {
      uploadUrl,
      uploadMethod,
      photoPdfUrl,
      contentType: raw.contentType?.trim() || contentType,
    };
  }

  async uploadPatrolCheckpointPhotoForm(
    workOrderId: number,
    projectZoneId: number,
    file: File,
  ): Promise<string> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const contentType = file.type?.trim() || 'application/pdf';
    const formData = new FormData();
    formData.append('file', file, file.name);
    const res = await firstValueFrom(
      this.http.post<OneApiResponse<{ photoPdfUrl?: string }>>(
        `${BASE}/f2/work-orders/${workOrderId}/patrol-checkpoint-photo`,
        formData,
        {
          headers: { 'X-Organization-Id': String(orgId) },
          params: {
            projectId: String(projectId),
            projectZoneId: String(projectZoneId),
            contentType,
          },
        },
      ),
    );
    const photoPdfUrl = unwrapData(res)?.photoPdfUrl;
    if (!photoPdfUrl) {
      throw new Error('Patrol photo upload did not return photoPdfUrl');
    }
    return photoPdfUrl;
  }

  async uploadChecklistPhotoForm(
    workOrderId: number,
    checklistOrder: number,
    file: File,
  ): Promise<string> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    const contentType = file.type?.trim() || 'image/jpeg';
    const formData = new FormData();
    formData.append('file', file, file.name);
    const res = await firstValueFrom(
      this.http.post<OneApiResponse<{ imageUrl?: string }>>(
        `${BASE}/${this.workOrderTierPath()}/work-orders/${workOrderId}/checklist-photo`,
        formData,
        {
          headers: { 'X-Organization-Id': String(orgId) },
          params: {
            projectId: String(projectId),
            checklistOrder: String(checklistOrder),
            contentType,
          },
        },
      ),
    );
    const imageUrl = unwrapData(res)?.imageUrl;
    if (!imageUrl) {
      throw new Error('Photo upload did not return imageUrl');
    }
    return imageUrl;
  }

  private async postAction(
    workOrderId: number,
    action: string,
    body?: RecordChecklistResponsesRequest | WorkOrderZoneScanRequest,
  ): Promise<FmWorkOrderDto> {
    const orgId = this.requireOrgId();
    const projectId = await this.requireProjectId();
    try {
      const res = await firstValueFrom(
        this.http.post<OneApiResponse<FmWorkOrderDto>>(
          `${BASE}/${this.workOrderTierPath()}/work-orders/${workOrderId}/${action}`,
          body ?? null,
          {
            headers: { 'X-Organization-Id': String(orgId) },
            params: { projectId: String(projectId) },
          },
        ),
      );
      const data = unwrapData(res);
      if (!data) {
        throw new Error('Request failed');
      }
      return data;
    } catch (err) {
      throw new Error(readFacilityApiError(err));
    }
  }

  private workOrderTierPath(): 'f1' | 'f2' {
    return this.isF1Manager() ? 'f1' : 'f2';
  }

  private requireOrgId(): number {
    const user = this.auth.getStoredUser();
    const orgId = user?.organizationId != null ? Number(user.organizationId) : NaN;
    if (!Number.isFinite(orgId) || orgId <= 0) {
      throw new Error('Organization context is required');
    }
    return orgId;
  }

  private async requireProjectId(): Promise<number> {
    const projectId = await this.gateDuty.resolveProjectId();
    if (projectId == null) {
      throw new Error('Project context is required for work orders');
    }
    return projectId;
  }
}
